import { useEffect, useState } from "react";
import { trans } from "@comps/components";
import { convertMinutesToHours } from "@comps/commonFunctions";
import { DownOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Const } from "../../constants";
import "@mods/common/style.css";
import "../style.css";

const langKey = Const["myAttendance"].lngKey;

const MINUTE_KEYS = ["worked_minutes", "expected_minutes", "short_minutes", "break_minutes"];

// Each metric wears the colour of the legend badge it belongs to, so the cards and
// the row markers read as one system (values below mirror the badge classes in
// modules/common/style.css).
const KEY_COLORS = {
  presents: "#198754",          // P — bg-success
  worked_minutes: "#198754",
  over_times: "#3f7d58",
  missings: "#dc3545",          // M — bg-danger
  absents: "#a02622",           // A — bg-error dker
  short_durations: "#ffc100",   // S — bg-orange
  short_minutes: "#ffc100",
  late_arrivals: "#faad14",     // bg-warning
  early_lefts: "#faad14",
  day_offs: "#36c2aa",          // DO — bg-day-off
  rest_days: "#5368a2",         // R — bg-default-time-slot
  holidays: "#616f77",          // H — bg-tr-black
  relaxations: "#ff5722",       // R — bg-informed-late
  official_duties: "#333333",   // OD — bg-darkgray
  leaves: "#107863",            // L — bg-greensea dker
  travels: "#f5791a",           // T — bg-info
};

const NEUTRAL_COLOR = "#8c8378";

const hexToRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const full = value.length === 3 ? value.split("").map((c) => c + c).join("") : value;
  const num = parseInt(full, 16);

  return `rgba(${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}, ${alpha})`;
};

const legends = [
  ["P", "Present", "bg-success"],
  ["M", "Missing", "bg-danger"],
  ["A", "Absent", "bg-error dker"],
  ["S", "Short Duration", "bg-orange"],
  ["DO", "Day Off", "bg-day-off"],
  ["R", "Rest Day", "bg-default-time-slot"],
  ["H", "Holiday", "bg-tr-black dker"],
  ["R", "Relaxation", "bg-informed-late"],
  ["OD", "Official Duty", "bg-darkgray"],
  ["L", "Leave", "bg-greensea dker"],
  ["T", "Travel", "bg-info"],
  ["W", "Weekend", "badge-empty bg-f5 display-inline"],
];

const formatValue = (key, value) => (MINUTE_KEYS.includes(key) ? convertMinutesToHours(value) : value);

// a metric that scored zero stays neutral instead of shouting its badge colour
const getMetaStyle = (key, value) => {
  const isZero = `${value}`.trim() === "0";
  const color = (!isZero && KEY_COLORS[key]) || NEUTRAL_COLOR;

  return {
    borderColor: hexToRgba(color, 0.35),
    borderLeftColor: color,
    background: `linear-gradient(135deg, #ffffff, ${hexToRgba(color, 0.12)})`,
    "--ts-meta-value-color": color,
  };
};

const TimeSheetSummary = (props) => {
  const [summary, setSummary] = useState();
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setSummary({
      ...props.record,
    });
    // eslint-disable-next-line
  }, [props.attendanceSummary, props.record]);

  if (!summary || !props.employee) return null;

  const keys = Object.keys(summary);
  const range = props.dates && props.dates.from
    ? `${dayjs(props.dates.from).format("DD MMM")} - ${dayjs(props.dates.end).format("DD MMM YYYY")}`
    : null;

  return (
    <div className={ `ts-sumbar${isExpanded ? " is-open" : ""}` }>
      <div
        className="ts-sumtop"
        role="button"
        tabIndex={ 0 }
        onClick={ () => setIsExpanded(!isExpanded) }
        onKeyDown={ (event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setIsExpanded(!isExpanded);
          }
        } }
      >
        <div className="ts-sumtop-left">
          <span className="ts-sumtop-name">{ props.employee }</span>
          <div className="ts-sumtop-quick">
            { range ? <span>{ range }</span> : null }
            { summary.presents !== undefined ? <span>{ trans(`${langKey}.presents`) } <b>{ summary.presents }</b></span> : null }
            { summary.late_arrivals !== undefined ? <span>{ trans(`${langKey}.late_arrivals`) } <b>{ summary.late_arrivals }</b></span> : null }
            { summary.worked_minutes !== undefined ? <span>{ trans(`${langKey}.worked_minutes`) } <b>{ convertMinutesToHours(summary.worked_minutes) }</b></span> : null }
          </div>
        </div>
        <button type="button" className="ts-sumtop-toggle" aria-expanded={ isExpanded }>
          { isExpanded ? "Collapse" : "Expand" }
          <span className="ts-chev" aria-hidden="true"><DownOutlined /></span>
        </button>
      </div>

      <div className="ts-sumbody">
        <div className="ts-sumbody-inner">
          <div className="ts-metagrid">
            { keys.map((key) => (
              <div className="ts-meta" key={ key } style={ getMetaStyle(key, summary[key]) }>
                <div className="ts-meta-label">{ trans(`${langKey}.${key}`) }</div>
                <div className="ts-meta-value">{ formatValue(key, summary[key]) }</div>
              </div>
            )) }
            { props.manuallyUpdatedCount !== undefined ? (
              <div className="ts-meta" key="manually-updated" style={ getMetaStyle('manually_updated', props.manuallyUpdatedCount || 0) }>
                <div className="ts-meta-label">Manually Updated</div>
                <div className="ts-meta-value">{ props.manuallyUpdatedCount || 0 }</div>
              </div>
            ) : null }
          </div>

          <div className="ts-legend">
            { legends.map(([mark, label, className]) => (
              <span key={ `${mark}-${label}` }>
                <i className={ `badge badge-single ${className}` }>{ mark }</i>
                { label }
              </span>
            )) }
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSheetSummary;
